import React, { useContext } from 'react';

import styled from '@emotion/styled';

import useAssignDriver from '../../../../hooks/dashboard/driver-list/useAssignDriver';
import useSearchDriver from '../../../../hooks/dashboard/driver-list/useSearchDriver';
import useVehicleLicensePlate from '../../../../hooks/dashboard/driver-list/useVehicleLicensePlate';
import {
  AssignVehicleDataType,
  AssignVehicleInitialValues,
  AssignVehicleSchema,
} from '../../../../models/dashboard/driver-list/assign-vehicle';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import DriverCard from '../../../Cards/DriverCard';
import ValueCard from '../../../Cards/ValueCard';
import { CustomSelectField } from '../../../FormCom/CustomSelectField';
import CustomSelectSearchAsyncField from '../../../FormCom/CustomSelectSearchAsyncField';
import { FieldControllerSync } from '../../../FormCom/FieldControllerSync';
import { FormComponent } from '../../../FormCom/FormComponent';
import { TextInputField } from '../../../FormCom/FormField';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';

interface AssignVehicleModalProps {
  data?: any;
}

const AssignVehicleModal: React.FC<AssignVehicleModalProps> = ({ data }) => {
  const modalContext = useContext(ModalContext);
  const [selectedDriver, setSelectedDriver] = React.useState<any>(null);
  const { assignDriver, loading } = useAssignDriver();
  const {
    searchDriver,
    loading: searchLoading,
    fetchedDriverData,
  } = useSearchDriver();

  const {
    searchVehicleLicensePlate,
    loading: searchVehicleLoading,
    fetchedVehicleLicenseData,
  } = useVehicleLicensePlate();

  const onSubmit = (values: AssignVehicleDataType) => {
    if (data?.employmentType) {
      values.employmentType = data.employmentType;
    }
    assignDriver(values);
  };

  return (
    <ModalContainer label='Assign Vehicles to Drivers'>
      <FormComponent
        initialValues={AssignVehicleInitialValues}
        schema={AssignVehicleSchema}
        onSubmit={onSubmit}
      >
        <CustomSelectSearchAsyncField
          name='driverId'
          id='driverId'
          label='Driver Name'
          renderComponent={(data) => <DriverCard data={data} />}
          fetchDataFn={(search) => searchDriver(search, false)}
          fetchDataLoading={searchLoading}
          fetchedData={fetchedDriverData}
          requiredDataKey='id'
          presentationDataKey='name'
          placeholder='Select Driver'
          onSelect={(data) => setSelectedDriver(data)}
        />

        <FieldControllerSync
          fieldName='employmentType'
          fieldValue={selectedDriver?.employmentType}
        />

        {selectedDriver?.employmentType != null ? (
          <TextInputField
            type='text'
            name='employmentType'
            id='employmentType'
            label='Employment type:'
            placeholder={selectedDriver.employmentType}
            readonly
          />
        ) : (
          <CustomSelectField
            label='Employment type:'
            name='employmentType'
            id='employmentType'
            placeholder='Employment type'
            optionData={['Gig', 'Exclusive']}
          />
        )}

        <FieldControllerSync
          fieldName='plateNumber'
          fieldValue={data?.fleetPlateNumber}
        />

        {data?.fleetPlateNumber != null ? (
          <TextInputField
            type='text'
            name='plateNumber'
            id='plateNumber'
            label='Vehicle License Plate'
            placeholder={data.fleetPlateNumber}
            readonly
          />
        ) : (
          <CustomSelectSearchAsyncField
            name='plateNumber'
            id='plateNumber'
            label='Vehicle License Plate'
            renderComponent={(data) => <ValueCard data={data} />}
            fetchDataFn={searchVehicleLicensePlate}
            fetchDataLoading={searchVehicleLoading}
            fetchedData={fetchedVehicleLicenseData}
            requiredDataKey='fleetPlateNumber'
            presentationDataKey='fleetPlateNumber'
            placeholder='Input license plate number'
          />
        )}

        <ButtonArea>
          <FormSubmitButton
            className='clear fill'
            btnType='button'
            onClick={() => modalContext?.setShowModal(false)}
          >
            Cancel
          </FormSubmitButton>
          <FormSubmitButton loading={loading} className='fill'>
            Assign Vehicle
          </FormSubmitButton>
        </ButtonArea>
      </FormComponent>
    </ModalContainer>
  );
};

const ButtonArea = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
`;

export default AssignVehicleModal;
